import {
    AssetPaginationParamDto,
    CreateFileDto as InternalCreateFileDto,
    CreateFolderDto as InternalCreateFolderDto,
    UpdateAssetDto as InternalUpdateAssetDto,
    ViewFileDto as InternalViewFileDto,
    ViewFolderDto as InternalViewFolderDto,
    CreateFolderDtoTypeEnum,
    ViewFolderDtoTypeEnum,
    CreateFileDtoTypeEnum,
    ViewFileDtoTypeEnum,
    ViewFolderDtoMajorityChildTypeEnum,
} from '@deltasierra/microservice-clients-asset-library';
import { ApiRoute, HttpMethod } from '../common/api/routes';
import { Client } from '../client/Client';
import { ClientId } from '../client/ClientId';
import { Location, LocationId } from '../location/Location';
import { Omit } from '../common/lang/types';
import { AssetId } from './models';

export type CanCreateAssetParams = {
    modelType: 'client' | 'location';
    modelId: ClientId | LocationId;
};
export type PermissionsDto = {
    permissionLevel: 'client' | 'location';
    sharedWithIds: ClientId[] | LocationId[];
};

export type ViewAssetDto = ViewFileDto | ViewFolderDto;
export type ViewFolderDto = AssetIdDto &
    Omit<InternalViewFolderDto, 'id' | 'majorityChildType' | 'type'> &
    PermissionsDto & { type: FolderType; majorityChildType: FileType };
export type ViewFileDto = AssetIdDto & Omit<InternalViewFileDto, 'id' | 'type'> & PermissionsDto & { type: FileType };

export type AssetLibraryViewAssetDto = AssetLibraryViewFileDto | AssetLibraryViewFolderDto;
export type AssetLibraryViewFolderDto = InternalViewFolderDto;
export type AssetLibraryViewFileDto = InternalViewFileDto;
// Export type ViewDetailedFolderDto = InternalViewDetailedFolderDto;

export type AssetIdDto = { id: AssetId };
export type AssetIdsDto = { assetIds: AssetId[] };

export type CreateAssetDto = CreateFileDto | CreateFolderDto;
export type CreateFolderDto = Omit<InternalCreateFolderDto, 'type'> & PermissionsDto & { type: FolderType };
export type CreateFileDto = Omit<InternalCreateFileDto, 'type'> & PermissionsDto;

export type SearchAssetsDto = AssetPaginationParamDto & { locationId: LocationId; parentId?: AssetId };

export type UpdateAssetDto = InternalUpdateAssetDto &
    Omit<PermissionsDto, 'permissionLevel' | 'sharedWithIds'> & {
        sharedWithIds?: ClientId[] | LocationId[];
    };

export type UpdateAssetWithIdDto = UpdateAssetDto & { id: AssetId };
export type UpdateAssetsDto = { assets: UpdateAssetDto[] };

export type LocationIdParams = { locationId: LocationId };
export type SimpleClient = Pick<Client, 'id' | 'title'> & { numCollections: number };
export type GetNumCollectionsBody = { modelType: 'client' | 'location'; modelIds: ClientId[] | LocationId[] };
export type ShareableLocation = ShareableClient & { locationId: Location['id']; locationTitle: Location['title'] };
export type ShareableClient = { clientId: Client['id']; clientTitle: Client['title']; numCollections: number };

export type FolderType = 'folder';
export type FileType = 'document' | 'image' | 'video';
export type CreateFolderEnumType = CreateFolderDtoTypeEnum.Folder;
export type ViewFolderEnumType = ViewFolderDtoTypeEnum.Folder;
export type CreateFileEnumType =
    | CreateFileDtoTypeEnum.Document
    | CreateFileDtoTypeEnum.Image
    | CreateFileDtoTypeEnum.Video;
export type ViewFileEnumType = ViewFileDtoTypeEnum.Document | ViewFileDtoTypeEnum.Image | ViewFileDtoTypeEnum.Video;
export type ViewFolderMajorityEnumType =
    | ViewFolderDtoMajorityChildTypeEnum.Document
    | ViewFolderDtoMajorityChildTypeEnum.Image
    | ViewFolderDtoMajorityChildTypeEnum.Video;

export function isFolder(asset: ViewAssetDto): asset is ViewFolderDto {
    return asset.type === 'folder';
}

const canCreateAsset = new ApiRoute<{ result: boolean }, CanCreateAssetParams, void, void>(HttpMethod.Get, path =>
    path
        .param('modelType')
        .param('modelId')
        .literal('asset-library')
        .literal('assets')
        .literal('permissions')
        .literal('create'),
);

const canEditAsset = new ApiRoute<{ result: boolean }, AssetIdDto, void, void>(HttpMethod.Get, path =>
    path.literal('asset-library').literal('assets').param('id').literal('permissions').literal('edit'),
);

const createAsset = new ApiRoute<ViewAssetDto, AssetIdDto, CreateAssetDto, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('collections').param('id').literal('assets'),
);

const createCollection = new ApiRoute<ViewAssetDto, void, CreateAssetDto, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('collections'),
);

const deleteCollection = new ApiRoute<ViewAssetDto, AssetIdDto, void, void>(HttpMethod.Delete, path =>
    path.literal('asset-library').literal('collections').param('id'),
);

const deleteAssets = new ApiRoute<void, void, AssetIdsDto, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('assets').literal('bulk').literal('delete'),
);

const recycleAssets = new ApiRoute<void, AssetIdDto, void, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('assets').literal('bulk').literal('recycle'),
);

const restoreAssets = new ApiRoute<void, AssetIdDto, void, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('assets').literal('bulk').literal('restore'),
);

const updateCollection = new ApiRoute<ViewAssetDto, AssetIdDto, UpdateAssetDto, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('collections').param('id'),
);

const updateAssets = new ApiRoute<ViewAssetDto[], void, UpdateAssetsDto, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('assets').literal('bulk').literal('update'),
);

const getAsset = new ApiRoute<ViewAssetDto, AssetIdDto, void, void>(HttpMethod.Get, path =>
    path.literal('asset-library').literal('assets').param('id'),
);

const getCollections = new ApiRoute<ViewFolderDto[], LocationIdParams, void, void>(HttpMethod.Get, path =>
    path.literal('asset-library').param('locationId').literal('collections'),
);

const getNumCollections = new ApiRoute<SimpleClient[], void, GetNumCollectionsBody, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('numCollections'),
);

const searchAssets = new ApiRoute<ViewAssetDto[], void, SearchAssetsDto, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('assets').literal('search').literal('unpaginated'),
);

const searchChildAssets = new ApiRoute<ViewFolderDto, AssetIdDto, SearchAssetsDto, void>(HttpMethod.Post, path =>
    path.literal('asset-library').literal('assets').param('id').literal('search').literal('unpaginated'),
);

/**
 * Get a list of clients that the user can share with. This list of sorted by client title. It will also include the
 * number of root assets that have been shared with the client. This endpoint will return an empty array ([]) when the
 * user doesn't have access or permission to share with any clients.
 *
 * @example
 * [
 *      { clientId: 1, clientTitle: 'Cats 4 you', numCollections: 5 },
 *      { clientId: 1, clientTitle: 'Dogs 4 you', numCollections: 6 }
 * ]
 */
const getShareableClients = new ApiRoute<ShareableClient[], void, void, void>(HttpMethod.Get, path =>
    path.literal('asset-library').literal('shareable-clients'),
);

/**
 * Get a list of locations that a user can share with. This list is sorted by client title (first), and location title
 * (second). It also includes how many root assets the location already has access to.
 *
 * @example
 * [
 *      { clientId: 1, clientTitle: 'Cats 4 you', locationId: 2, locationTitle: "Melbourne CBD", numCollections: 16 },
 *      { clientId: 1, clientTitle: 'Cats 4 you', locationId: 3, locationTitle: "Sydney CBD", numCollections: 10 },
 *      { clientId: 1, clientTitle: 'Dogs 4 you', locationId: 5, locationTitle: "Richmond", numCollections: 3 }
 * ]
 */
const getShareableLocations = new ApiRoute<ShareableLocation[], void, void, void>(HttpMethod.Get, path =>
    path.literal('asset-library').literal('shareable-locations'),
);

export const AssetLibraryEndpoints = {
    canCreateAsset,
    canEditAsset,
    createAsset,
    createCollection,
    deleteAssets,
    deleteCollection,
    getAsset,
    getCollections,
    getNumCollections,
    getShareableClients,
    getShareableLocations,
    recycleAssets,
    restoreAssets,
    searchAssets,
    searchChildAssets,
    updateAsset: updateCollection,
    updateAssets,
};
